import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { Card, CardTitle, CardDescription } from './Card';
import { Skeleton } from './EmptyState';
import { StatusPill } from './StatusPill';
import { api } from '@/lib/api';

interface Progress {
  project_id: string;
  total: number;
  open: number;
  working: number;
  completed: number;
  cancelled: number;
  percent_done: number;
}

export function ProjectProgressPanel({ projectID }: { projectID: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['project-progress', projectID],
    queryFn:  () => api<Progress>(`/projects/projects/${projectID}/progress`),
    enabled:  !!projectID,
  });

  if (!isLoading && (data?.total ?? 0) === 0) return null;

  return (
    <Card>
      <CardTitle>
        <Activity className="size-4 inline mr-1.5 text-accent" /> Progress
      </CardTitle>
      <CardDescription>Task completion roll-up. Cancelled tasks are excluded from the percentage.</CardDescription>

      {isLoading || !data ? (
        <div className="mt-4"><Skeleton className="h-10 w-full" /></div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-page-title num font-semibold text-ink">{data.percent_done}%</span>
            <span className="text-caption text-stone">{data.completed} of {data.total - data.cancelled} done</span>
          </div>
          <div className="h-2 rounded-full bg-hairline overflow-hidden">
            <div
              className="h-full bg-brand-green-deep transition-all"
              style={{ width: `${data.percent_done}%` }}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap text-caption">
            <StatusPill tone="neutral"  withDot={false}>{data.open} open</StatusPill>
            <StatusPill tone="info"     withDot={false}>{data.working} working</StatusPill>
            <StatusPill tone="success"  withDot={false}>{data.completed} completed</StatusPill>
            {data.cancelled > 0 && (
              <StatusPill tone="danger" withDot={false}>{data.cancelled} cancelled</StatusPill>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
